import { Backdrop, Box, Button, Fade, Grid, Modal, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';

import GlobalDialog from '../../components/@extended/GlobalDialog';
import { CopierOverrideDialogProps } from '../../types/copier';
import ReactTable from '../../components/custom/ReactTableCustom';
import { useTheme } from '@mui/material/styles';
import { CheckOutlined, CloseOutlined, EditOutlined, MinusOutlined } from '@ant-design/icons';
import LoadingButton from '../../components/@extended/LoadingButton';
import { ReactComponent as DirectionIcon } from '../../assets/images/icons/direction.svg';
import { ReactComponent as TrashIcon } from '../../assets/images/icons/trash6.svg';
import { sizeByBreakpoint, textSizeByBreakpoint } from '../../utils/helper';
import { risk_type_map_value, filter_type_map_value } from './CopyTradingTableSection';
import { useTradeClient } from '../../client/client-hook';
import useAuth from '../../hooks/useAuth';
import {
  ListSymbolSettingsReq,
  ListSymbolSettingsReply,
  UpdateSymbolSettingsReq,
  DeleteSymbolSettingsReq
} from '../../proto/trade-copier_pb';
import { useDispatch } from '../../store';
import { DispatchSnackbarService } from '../../services';
import SymbolSettingsForm from './components/SymbolSettingsForm';
import MainCard from '../../components/MainCard';
import IconButton from '../../components/@extended/IconButton';

const service = new DispatchSnackbarService();

const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  borderRadius: '8px',
  padding: '4px'
};

const CopierOverrideDialog = ({ isOpen, onClose, selectedSlave }: CopierOverrideDialogProps) => {
  const theme = useTheme();
  const { user } = useAuth();
  const tradeClient = useTradeClient();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [tradingSettingsData, setTradingSettingsData] = useState<any[]>([]);
  const [pageSize, setPageSize] = useState(5);
  const [pageIndex, setPageIndex] = useState(0);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedSettings, setSelectedSettings] = useState<any>(null);
  const [hasUnsaved, setHasUnsaved] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);

  const riskTypeMap: any = {
    FIXED_LOT: 'Fixed Lot',
    LOT_MULTIPLIER: 'Lot Multiplier',
    BALANCE_MULTIPLIER: 'Balance Multiplier',
    FIXED_BALANCE_MULTIPLIER: 'Fixed Balance Multiplier',
    BALANCE_PERCENTAGE: 'Fixed % risk'
  };

  const fetchSymbolSettings = async () => {
    if (!user?.tcId || !selectedSlave?.id) return;

    setLoading(true);
    try {
      const client = await tradeClient();
      const req: ListSymbolSettingsReq.AsObject = {
        userId: user.tcId,
        slaveId: selectedSlave.id
      };

      const res = await client.listSymbolSettings(req);
      const result = res as ListSymbolSettingsReply.AsObject;

      if (result?.symbolSettingsList) {
        const mappedData = result.symbolSettingsList.map((setting) => {
          const riskType = setting.riskType?.value || 'UNKNOWN';
          let riskSetting = '-';

          switch (riskType) {
            case 'FIXED_LOT':
              riskSetting = setting.fixedLotValue?.value?.toString() + ' Lot' || '0 Lot';
              break;
            case 'LOT_MULTIPLIER':
              riskSetting = setting.lotMultiplierValue?.value?.toString() + '%' || '0%';
              break;
            case 'BALANCE_MULTIPLIER':
              riskSetting = setting.balanceMultiplierValue?.value?.toString() + '%' || '0%';
              break;
            case 'FIXED_BALANCE_MULTIPLIER':
              riskSetting = `$${setting.fixedMasterBalance?.value?.toString() || '0'} | ${
                setting.fixedBalanceMultiplierValue?.value?.toString() || '0'
              }%`;

              break;
            case 'BALANCE_PERCENTAGE':
              riskSetting = setting.balancePercentageValue?.value?.toString() + '%' || '0%';
              break;
          }

          let status: 'left' | 'right' | 'center' = 'left';
          if (!setting.enabled?.value && !setting.manageExisting?.value) {
            status = 'left';
          } else if (setting.enabled?.value && !setting.manageExisting?.value) {
            status = 'right';
          } else if (!setting.enabled?.value && setting.manageExisting?.value) {
            status = 'center';
          }

          return {
            id: setting.id,
            masterSymbol: setting.masterSymbol || '-',
            slaveSymbol: setting.symbol || '-',
            riskType: riskTypeMap[riskType] || riskType,
            riskSetting,
            status: status,
            pauseTimeSec: setting.pauseUntil?.seconds || 0,
            rawData: setting,
            // Full SymbolSettingTableDto format for editing
            accountName: selectedSlave?.accountName || '',
            isMaster: false,
            copyFrom: selectedSlave?.copyFrom || '',
            copyFromId: selectedSlave?.copyFromId || '',
            accountId: selectedSlave?.accountId || '',
            actualAccountId: selectedSlave?.actualAccountId || '',
            selected: false,
            placedTypeOptions: selectedSlave?.placedTypeOptions || [],
            masterAccountNumber: selectedSlave?.masterAccountNumber || '',
            additionalProps: {
              riskType: setting.riskType?.value,
              copyStopLoss: setting.copyStopLoss?.value || '',
              copyTakeProfit: setting.copyTakeProfit?.value || false,
              lotMultiplierValue: setting.lotMultiplierValue?.value || 0,
              fixedLotValue: setting.fixedLotValue?.value || 0,
              copyPendingOrders: setting.copyPendingOrders?.value || false,
              // `as any`: the symbol-setting proto type omits this account-level flag, but the
              // wire message can still carry it — read it through so an edit round-trip can't zero it.
              copyExistingTrades: (setting as any).copyExistingTrades?.value || false,
              forceMinimumLotSize: setting.forceMinimumLotSize?.value || false,
              forceMaximumLotSize: setting.forceMaximumLotSize?.value || false,
              copyComment: setting.copyComment?.value || false,
              customComment: setting.customComment?.value || '',
              platform: selectedSlave?.slaveData?.platform || '',
              reverseTrade: setting.reverseTrade?.value || false,
              masterId: selectedSlave?.copyFromId || '',
              strictClose: setting.strictClose?.value || false,
              considerContractSize: setting.considerContractSize?.value || false,
              copyMagicNumber: setting.copyMagicNumber?.value || false,
              magicNumber: setting.magicNumber?.value === 0 ? '0' : setting.magicNumber?.value || '',
              fixedMasterBalance: setting.fixedMasterBalance?.value || '',
              fixedBalanceMultiplierValue: setting.fixedBalanceMultiplierValue?.value || '',
              balanceMultiplierValue: setting.balanceMultiplierValue?.value || 0,
              balancePercentageValue: setting.balancePercentageValue?.value || 0,
              lotRefiner: setting.lotRefinerValue?.value,
              tradeDelayMin: setting.minDelayTime?.value,
              tradeDelayMax: setting.maxDelayTime?.value,
              magicFilterList: setting.magicFiltersList || [],
              magicFilter: (setting.magicFiltersList || [])
                .filter((e: any) => e.value !== null && e.value !== undefined && e.value !== 0)
                .map((data: any) => data.value),
              commentFilterList: setting.commentFiltersList || [],
              commentFilter: (setting.commentFiltersList || [])
                .filter((e: any) => e.value !== null && e.value !== undefined && e.value !== '')
                .map((data: any) => data.value),
              minLotFilter: setting.minLotFilter?.value,
              maxLotFilter: setting.maxLotFilter?.value,
              copyExpiryTime: setting.copyExpiryTime?.value,
              fixedStopLossValue: setting.stopLossPip?.value || 0,
              fixedTakeProfitValue: setting.takeProfitPip?.value || 0,
              stopLossRefinementValue: setting.slRefiner?.value || 0,
              takeProfitRefinementValue: setting.tpRefiner?.value || 0
            },
            settings: setting
          };
        });

        setTradingSettingsData(mappedData);
      }
    } catch (error) {
      console.error('Error fetching symbol settings:', error);
      setTradingSettingsData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && selectedSlave) {
      fetchSymbolSettings();
    }
  }, [isOpen, selectedSlave]);

  const deleteOverride = async (row: any) => {
    if (!user?.tcId || !selectedSlave?.id) return;
    try {
      setLoading(true);
      const client = await tradeClient();
      const req: DeleteSymbolSettingsReq.AsObject = {
        slaveId: selectedSlave.id,
        userId: user.tcId,
        mappingId: row.id
      };

      await client.deleteSymbolSettings(req);

      service.dispatchSnackbar(dispatch, 'Symbol override deleted successfully', 'alert', 'success', 2000);

      // Refresh the list
      await fetchSymbolSettings();
      setLoading(false);
    } catch (error: any) {
      console.error('Error deleting symbol settings:', error);
      const msz = error.message === 'no permission to access this RPC' ? 'Permission denied' : error.message;
      service.dispatchSnackbar(dispatch, msz, 'alert', 'error', 5000);
      setLoading(false);
    }
  };

  interface ThreeStateToggleProps {
    value: 'active' | 'paused' | 'disabled';
    onChange?: (newValue: 'active' | 'paused' | 'disabled') => void;
    disabled?: boolean;
  }

  const ThreeStateToggle = ({ value, onChange, disabled = false }: ThreeStateToggleProps) => {
    const getBackgroundColor = () => {
      switch (value) {
        case 'active':
          return theme.palette.success.main;
        case 'paused':
          return theme.palette.warning.main;
        case 'disabled':
          return theme.palette.grey[400];
        default:
          return theme.palette.grey[400];
      }
    };

    const getIcon = () => {
      switch (value) {
        case 'active':
          return <CheckOutlined style={{ fontSize: '13px', fontWeight: 'bold', strokeWidth: '50px' }} />;
        case 'paused':
          return <MinusOutlined style={{ fontSize: '10px' }} />;
        case 'disabled':
          return <CloseOutlined style={{ fontSize: '8px' }} />;
        default:
          return <CloseOutlined style={{ fontSize: '8px' }} />;
      }
    };

    const getThumbPosition = () => {
      switch (value) {
        case 'disabled':
          return '2px';
        case 'paused':
          return '21px';
        case 'active':
          return '40px';
        default:
          return '2px';
      }
    };

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (disabled || !onChange) return;

      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const toggleWidth = rect.width;

      const leftThird = toggleWidth / 3;
      const rightThird = (toggleWidth / 3) * 2;

      let newState: 'active' | 'paused' | 'disabled';
      if (clickX < leftThird) {
        newState = 'disabled';
      } else if (clickX < rightThird) {
        newState = 'paused';
      } else {
        newState = 'active';
      }
      onChange(newState);
    };

    return (
      <Box
        onClick={handleClick}
        sx={{
          width: 60,
          height: 22,
          borderRadius: '11px',
          backgroundColor: getBackgroundColor(),
          position: 'relative',
          cursor: disabled ? 'default' : 'pointer',
          transition: 'background-color 0.3s ease',
          opacity: disabled ? 0.6 : 1,
          '&:hover': {
            opacity: disabled ? 0.6 : 0.9
          }
        }}
      >
        <Box
          sx={{
            width: 18,
            height: 18,
            borderRadius: '50%',
            backgroundColor: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            position: 'absolute',
            top: '2px',
            left: getThumbPosition(),
            transition: 'left 0.3s ease'
          }}
        >
          <Box sx={{ color: getBackgroundColor(), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{getIcon()}</Box>
        </Box>
      </Box>
    );
  };

  const columns = [
    {
      Header: 'Master Symbol',
      accessor: 'masterSymbol',
      Cell: ({ value }: any) => (
        <Typography color="textPrimary" fontWeight={600}>
          {value}
        </Typography>
      )
    },
    {
      Header: 'Slave Symbol',
      accessor: 'slaveSymbol',
      Cell: ({ value }: any) => (
        <Typography color={theme.palette.warning.main} fontWeight={600}>
          {value}
        </Typography>
      )
    },
    {
      Header: 'Risk Type',
      accessor: 'riskType',
      Cell: ({ value }: any) => (
        <Typography color="textSecondary" fontWeight={500}>
          {value}
        </Typography>
      )
    },
    {
      Header: 'Risk Setting',
      accessor: 'riskSetting',
      Cell: ({ value }: any) => (
        <Typography color="textSecondary" fontWeight={500}>
          {value}
        </Typography>
      )
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ row: { original } }: any) => {
        const getStatus = (): 'active' | 'paused' | 'disabled' => {
          if (original.pauseTimeSec && original.pauseTimeSec * 1000 > new Date().getTime()) {
            return 'paused';
          }
          if (original.status === 'right') return 'active';
          if (original.status === 'center') return 'paused';
          return 'disabled';
        };
        const handleStatusChange = async (newValue: 'active' | 'paused' | 'disabled') => {
          if (!user?.tcId || !selectedSlave?.id) return;
          // Determine new status value
          const newStatus = newValue === 'active' ? 'right' : newValue === 'paused' ? 'center' : 'left';
          // Optimistically update the UI
          setTradingSettingsData((prev) => prev.map((item) => (item.id === original.id ? { ...item, status: newStatus } : item)));
          try {
            const client = await tradeClient();
            const setting = original.rawData;
            // Build the request matching the exact structure from SymbolSettingsTableSection
            const req: UpdateSymbolSettingsReq.AsObject = {
              userId: user.tcId,
              configId: selectedSlave.configId || '',
              slaveId: selectedSlave.id,
              masterSymbolName: setting.masterSymbol || '',
              slaveSymbolName: setting.symbol || '',
              riskType: risk_type_map_value[setting.riskType?.value || 'UNKNOWN'],
              lotMultiplierValue: setting.lotMultiplierValue,
              fixedLotValue: setting.fixedLotValue,
              balanceMultiplierValue: setting.balanceMultiplierValue,
              copyTakeProfit: setting.copyTakeProfit,
              copyStopLoss: setting.copyStopLoss,
              copyPendingOrders: setting.copyPendingOrders,
              forceMinimumLotSize: setting.forceMinimumLotSize,
              forceMaximumLotSize: setting.forceMaximumLotSize,
              copyComment: setting.copyComment,
              customComment: setting.customComment,
              reverseTrade: setting.reverseTrade,
              strictClose: setting.strictClose,
              considerContractSize: setting.considerContractSize,
              copyMagicNumber: setting.copyMagicNumber,
              magicNumber: setting.magicNumber,
              lotRefinerValue: setting.lotRefinerValue,
              magicFiltersList: setting.magicFiltersList || [],
              copyExpiryTime: setting.copyExpiryTime,
              commentFiltersList: setting.commentFiltersList || [],
              minLotFilter: setting.minLotFilter,
              maxLotFilter: setting.maxLotFilter,
              minDelayTime: setting.minDelayTime,
              maxDelayTime: setting.maxDelayTime,
              fixedMasterBalance: setting.fixedMasterBalance,
              fixedBalanceMultiplierValue: setting.fixedBalanceMultiplierValue,
              slRefiner: setting.slRefiner,
              tpRefiner: setting.tpRefiner,
              stopLossPip: setting.stopLossPip,
              takeProfitPip: setting.takeProfitPip,
              balancePercentageValue: setting.balancePercentageValue,
              allowedTradeSide: setting.allowedTradeSide,
              commentFilterType: filter_type_map_value[setting.commentFilterType?.value || 'UNKNOWN'],
              magicFilterType: filter_type_map_value[setting.magicFilterType?.value || 'UNKNOWN']
            };

            // Set status-specific values
            if (newValue === 'disabled') {
              req.manageExisting = { value: false };
              req.enable = { value: false };
            } else if (newValue === 'active') {
              req.manageExisting = { value: false };
              req.enable = { value: true };
            } else if (newValue === 'paused') {
              req.manageExisting = { value: true };
              req.enable = { value: false };
            }

            await client.overrideSymbolSettings(req);

            // Success message based on status
            const message =
              newValue === 'disabled'
                ? 'Symbol override disabled successfully'
                : newValue === 'paused'
                ? 'Symbol override disabled - managing existing'
                : 'Symbol override enabled successfully';

            service.dispatchSnackbar(dispatch, message, 'alert', 'success', 2000);

            // Refresh the list to show updated status
            await fetchSymbolSettings();
          } catch (error) {
            console.error('Error updating symbol settings:', error);

            // Revert on error
            setTradingSettingsData((prev) => prev.map((item) => (item.id === original.id ? { ...item, status: original.status } : item)));
            const msz =
              error instanceof Error && error.message === 'no permission to access this RPC'
                ? 'Permission denied'
                : error instanceof Error
                ? error.message
                : 'An error occurred';
            service.dispatchSnackbar(dispatch, msz, 'alert', 'error', 5000);
          }
        };

        return (
          <ThreeStateToggle
            value={getStatus()}
            onChange={handleStatusChange}
            disabled={original.pauseTimeSec && original.pauseTimeSec * 1000 > new Date().getTime()}
          />
        );
      }
    },
    {
      Header: 'Settings',
      Cell: ({ row }: any) => (
        <Grid display="flex" gap={1}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setSelectedSettings(row.original);
              setOpenEdit(true);
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '2px',
              fontSize: textSizeByBreakpoint(14),
              fontWeight: 600,
              borderRadius: '6px',
              border: (theme) => `1px solid ${theme.palette.border.main}`,
              backgroundColor: (theme) => theme.palette.background.paper,
              color: (theme) => theme.palette.text.secondary,
              mr: 1,
              '&:hover': {
                borderColor: theme.palette.primary.main
              }
            }}
          >
            <EditOutlined style={{ fontSize: '16px', color: theme.palette.primary.main }} />
            Edit
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => deleteOverride(row.original)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '2px',
              fontSize: textSizeByBreakpoint(14),
              fontWeight: 600,
              borderRadius: '6px',
              border: (theme) => `1px solid ${theme.palette.border.main}`,
              backgroundColor: (theme) => theme.palette.background.paper,
              color: (theme) => theme.palette.text.secondary,
              mr: 1,
              '&:hover': {
                borderColor: theme.palette.primary.main
              }
            }}
          >
            <Box
              component={TrashIcon}
              sx={{
                fontSize: sizeByBreakpoint(1, 16),
                '& path': {
                  fill: theme.palette.primary.main
                }
              }}
            />
            Delete
          </Button>
        </Grid>
      )
    }
  ];

  return (
    <>
      <GlobalDialog
        open={isOpen}
        onClose={onClose}
        title={`Symbol config override (${selectedSlave?.accountName || 'Slave Account'})`}
        subtitle="Showing the list of all the accounts."
        footer={
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
            <LoadingButton
              sx={{
                maxWidth: 'max-content',
                paddingX: { xs: 1.2, sm: 1.4, md: 1.6 },
                paddingY: { xs: 0.8, sm: 0.9, md: 1 },
                borderRadius: '6px',
                borderColor: 'transparent',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '5px',
                fontSize: textSizeByBreakpoint(14),
                height: { xl: 44, lg: 42, md: 40, sm: 38, xs: 36 }
              }}
              loadingPosition="end"
              endIcon={<></>}
              loading={false}
              disableElevation
              disabled={false}
              fullWidth
              type="submit"
              variant="contained"
              color="primary"
              onClick={() => setOpenAdd(true)}
            >
              <DirectionIcon />
              Add Override
            </LoadingButton>
          </Box>
        }
        maxWidth="md"
      >
        <ReactTable
          hidePagination={false}
          isLoading={loading}
          columns={columns}
          data={tradingSettingsData.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize)}
          dataCount={tradingSettingsData.length}
          pageSize={pageSize}
          pageIndex={pageIndex}
          handlePageSize={(size) => {
            setPageSize(size);
            setPageIndex(0);
          }}
          handlePageChange={(direction) => {
            if (direction === 'next') {
              setPageIndex(pageIndex + 1);
            } else {
              setPageIndex(Math.max(0, pageIndex - 1));
            }
          }}
          getHeaderProps={(column: any) => column.getSortByToggleProps()}
        />
      </GlobalDialog>

      <GlobalDialog
        open={openAdd}
        onClose={() => {
          if (hasUnsaved) {
            setShowConfirmClose(true);
            return;
          }
          setOpenAdd(false);
        }}
        title="Override Settings"
        subtitle="Configure symbol-specific trading settings."
        maxWidth="sm"
      >
        <SymbolSettingsForm
          isEdit={false}
          showWrapper={false}
          handleClose={(val) => {
            if (val) {
              setShowConfirmClose(true);
              return;
            }
            setOpenAdd(false);
          }}
          checkHasUnsaved={(val) => {
            setHasUnsaved(val);
          }}
          handleSuccess={() => {
            fetchSymbolSettings();
            setOpenAdd(false);
          }}
          symbolSettings={null}
          selectedMasterAccountId={selectedSlave?.copyFromId || ''}
          selectedSlave={selectedSlave}
        />
      </GlobalDialog>

      <GlobalDialog
        open={openEdit}
        onClose={() => {
          if (hasUnsaved) {
            setShowConfirmClose(true);
            return;
          }
          setOpenEdit(false);
        }}
        title="Edit Override Settings"
        subtitle="Modify symbol-specific trading settings."
        maxWidth="sm"
      >
        <SymbolSettingsForm
          isEdit={true}
          showWrapper={false}
          handleClose={(val) => {
            if (val) {
              setShowConfirmClose(true);
              return;
            }
            setOpenEdit(false);
          }}
          checkHasUnsaved={(val) => {
            setHasUnsaved(val);
          }}
          handleSuccess={() => {
            fetchSymbolSettings();
            setOpenEdit(false);
          }}
          symbolSettings={selectedSettings}
          selectedMasterAccountId={selectedSlave?.copyFromId || ''}
          selectedSlave={selectedSlave}
        />
      </GlobalDialog>

      {showConfirmClose && (
        <Modal
          open={showConfirmClose}
          onClose={() => setShowConfirmClose(false)}
          closeAfterTransition
          BackdropComponent={Backdrop}
          BackdropProps={{
            timeout: 500
          }}
        >
          <Fade in={showConfirmClose}>
            <Box
              sx={{
                ...style,
                p: 0,
                width: { xs: '90%', sm: 420 },
                minWidth: 'auto',
                maxWidth: 520
              }}
            >
              <MainCard
                title={
                  <Typography fontWeight={700} fontSize={16}>
                    Unsaved Changes
                  </Typography>
                }
                secondary={
                  <IconButton
                    size="small"
                    onClick={() => {
                      setShowConfirmClose(false);
                    }}
                  >
                    <CloseOutlined />
                  </IconButton>
                }
              >
                <Typography variant="body1" sx={{ mb: 3 }}>
                  You have unsaved changes. Are you sure you want to close this form?
                </Typography>
                <Box display="flex" gap={2} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setShowConfirmClose(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setShowConfirmClose(false);
                      setOpenAdd(false);
                      setOpenEdit(false);
                      setHasUnsaved(false);
                    }}
                  >
                    Close Anyway
                  </Button>
                </Box>
              </MainCard>
            </Box>
          </Fade>
        </Modal>
      )}
    </>
  );
};

export default CopierOverrideDialog;
